import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from './api';
import { usePreferences } from './usePreferences';

export enum QualityIssueType {
  OVER_EXPOSED = 'overExposed',
  UNDER_EXPOSED = 'underExposed',
  NOT_SHARP = 'notSharp',
  GRAINY = 'grainy',
  OTHER = 'other',
}

export type ImagePathType = 'originalPath' | 'path' | 'sharpenedPath';

export interface ImageQualityAssessmentResponse {
  imageId: string;
  pathType?: ImagePathType;
  qualityRating?: number;
  qualityIssueTypes?: QualityIssueType[];
  note?: string;
  assessedAt?: string;
}

export interface UseImageQualityAssessmentParams {
  imageId?: string;
  pathType?: ImagePathType;
}

export interface ImageAssessmentRequestProps {
  qualityRating: number;
  qualityIssueTypes?: QualityIssueType[];
  note?: string;
}

export const useImageQualityAssessment = ({
  imageId,
  pathType,
}: UseImageQualityAssessmentParams) => {
  const user = useSelector((state: any) => state.login.user);
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  const preferences = usePreferences();
  const isImageQualityAssessmentEnabled =
    preferences?.data?.enableImageQualityAssessment;

  return useQuery<ImageQualityAssessmentResponse | null>(
    ['imageQualityAssessment', imageId, pathType],
    async () => {
      if (!imageId || !pathType) return null;

      return makeAPIRequestReactQuery<ImageQualityAssessmentResponse>(
        `images/assessments/${imageId}/${pathType}`,
        {},
        'GET',
        {
          headers: {
            Practice: currentPracticeId,
            Org: currentOrganizationId,
          },
        },
      );
    },
    {
      enabled: !!imageId && !!pathType && isImageQualityAssessmentEnabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );
};

export const useSubmitImageQualityAssessment = () => {
  const queryClient = useQueryClient();
  const user = useSelector((state: any) => state.login.user);
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useMutation({
    mutationFn: async ({
      imageId,
      pathType,
      data,
    }: {
      imageId: string;
      pathType: string;
      data: ImageAssessmentRequestProps;
    }) => {
      return makeAPIRequestReactQuery<ImageQualityAssessmentResponse>(
        `images/assessments/${imageId}/${pathType}`,
        {},
        'PUT',
        {
          headers: {
            Practice: currentPracticeId,
            Org: currentOrganizationId,
          },
          data,
        },
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch the assessment
      queryClient.invalidateQueries([
        'imageQualityAssessment',
        variables.imageId,
        variables.pathType,
      ]);
    },
  });
};
